/**
 * ContentToolsPage — Hook-Generator, Pain-Point-Analyse, Manychat,
 *                    Überarbeitete Entwürfe (Refresh-Draft-Workspace)
 *
 * Sektion "Überarbeitete Entwürfe" (Block 11):
 *   – Zeigt alle akzeptierten KI-Refresh-Entwürfe aus dem Evergreen-Workflow
 *   – Gate: evergreen (Creator+ / Trial)
 *   – Drafts können inline weiterbearbeitet, als bereit markiert oder gelöscht werden
 *   – Kein Autopublish, kein Publishing in diesem Block
 *
 * Backend-Endpunkte für Drafts:
 *   GET    /content-tools/refresh/drafts
 *   PATCH  /content-tools/refresh/drafts/{draft_id}
 *   DELETE /content-tools/refresh/drafts/{draft_id}
 *   POST   /content-tools/refresh/drafts/{draft_id}/add-to-plan  (Block 12)
 *
 * Block 12 — Monatsplan-Integration:
 *   ready_for_publish-Entwürfe können direkt in den Monatsplan eingetragen werden.
 *   Datum + Uhrzeit + Plattform wählbar. Kein Autopublish.
 */

import { useState, useEffect, useCallback } from "react";
import { NavLink } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Zap, TrendingUp, MessageSquare, Loader2, Search, Send, Copy,
  CheckCircle, FileText, Edit3, Trash2, RefreshCw, ArrowRight,
  ChevronDown, ChevronUp, Instagram, Facebook, Linkedin, Calendar,
  Sparkles, CalendarPlus,
} from "lucide-react";
import BrandContextHint from '../components/BrandContextHint';
import { useFeatureGate } from "../hooks/useFeatureGate";
import UpgradeNudge from "../components/UpgradeNudge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Status-Konfiguration für Refresh-Drafts ──────────────────────────────────
const DRAFT_STATUS_CONFIG = {
  draft:             { label: "KI-Entwurf",  bg: "bg-amber-100",  text: "text-amber-700"  },
  edited:            { label: "Bearbeitet",   bg: "bg-blue-100",   text: "text-blue-700"   },
  ready_for_publish: { label: "Bereit",       bg: "bg-green-100",  text: "text-green-700"  },
};

// ── Plattform-Icon ────────────────────────────────────────────────────────────
const PlatformIcon = ({ platform, size = 14 }) => {
  switch (platform?.toLowerCase()) {
    case "facebook":  return <Facebook  size={size} className="text-blue-600"  />;
    case "instagram": return <Instagram size={size} className="text-pink-500"  />;
    case "linkedin":  return <Linkedin  size={size} className="text-blue-700"  />;
    case "tiktok":    return <span style={{ fontSize: size }} className="font-bold leading-none">TT</span>;
    default:          return <Calendar  size={size} className="text-stone-400" />;
  }
};

// ── RefreshDraftCard ──────────────────────────────────────────────────────────
//
// Zeigt einen einzelnen Refresh-Entwurf mit:
//   - Collapse/Expand (Header-Klick)
//   - Herkunfts-Kontext (Aus Evergreen-Refresh, Begründung)
//   - Original vs. Entwurf side-by-side
//   - Weiterbearbeiten (Textarea) → Speichern
//   - Für Veröffentlichung vorbereiten (Status → ready_for_publish)
//   - In Monatsplan übernehmen (Block 12) → Inline-Formular mit Datum/Zeit/Plattform
//   - Entwurf löschen (DELETE)
//
// Kein Autopublish. Kein Rich-Text-Editor. Einfach und produktiv.

// Hilfsfunktion: Morgen als YYYY-MM-DD
const getTomorrowDate = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().split("T")[0];
};

const PLAN_TIMES = [
  "07:00","08:00","09:00","10:00","11:00","12:00",
  "13:00","14:00","15:00","16:00","17:00","18:00","19:00","20:00","21:00",
];

const RefreshDraftCard = ({ draft, onUpdate, onDelete }) => {
  const [expanded, setExpanded]       = useState(false);
  const [isEditing, setIsEditing]     = useState(false);
  const [editText, setEditText]       = useState(draft.draft_text);
  const [saving, setSaving]           = useState(false);
  const [localStatus, setLocalStatus] = useState(draft.status || "draft");

  // Block 12: Monatsplan-Formular
  const [showPlanForm, setShowPlanForm]     = useState(false);
  const [planDate, setPlanDate]             = useState(getTomorrowDate);
  const [planTime, setPlanTime]             = useState("10:00");
  const [planPlatform, setPlanPlatform]     = useState(draft.platform || "instagram");
  const [addingToPlan, setAddingToPlan]     = useState(false);
  const [inPlan, setInPlan]                 = useState(draft.linked_to_plan || false);

  const cfg = DRAFT_STATUS_CONFIG[localStatus] || DRAFT_STATUS_CONFIG.draft;

  const formattedDate = draft.created_at
    ? new Date(draft.created_at).toLocaleDateString("de-DE", {
        day: "2-digit", month: "2-digit", year: "numeric",
      })
    : "—";

  const handleSave = async () => {
    setSaving(true);
    try {
      await onUpdate(draft.draft_id, editText, "edited");
      setLocalStatus("edited");
      setIsEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const handleMarkReady = async () => {
    setSaving(true);
    try {
      await onUpdate(draft.draft_id, editText, "ready_for_publish");
      setLocalStatus("ready_for_publish");
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditText(draft.draft_text);
    setIsEditing(false);
  };

  // Block 12: Draft in Monatsplan eintragen
  const handleAddToPlan = async () => {
    if (!planDate) {
      toast.error("Bitte wähle ein Datum.");
      return;
    }
    setAddingToPlan(true);
    try {
      await axios.post(`${API}/content-tools/refresh/drafts/${draft.draft_id}/add-to-plan`, {
        scheduled_date: planDate,
        scheduled_time: planTime,
        platform:       planPlatform,
      });
      setInPlan(true);
      setShowPlanForm(false);
      const dateLabel = new Date(planDate + "T12:00:00").toLocaleDateString("de-DE", {
        day: "2-digit", month: "2-digit", year: "numeric",
      });
      toast.success(`Im Monatsplan eingetragen – ${dateLabel} um ${planTime} Uhr.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Eintragen in den Monatsplan.");
    } finally {
      setAddingToPlan(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-stone-200 overflow-hidden transition-shadow hover:shadow-sm">
      {/* ── Karten-Header ─────────────────────────────────────────────── */}
      <div
        className="p-4 cursor-pointer select-none"
        onClick={() => setExpanded(e => !e)}
      >
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            {/* Meta-Zeile */}
            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
              <PlatformIcon platform={draft.platform} size={13} />
              <span className="text-xs text-stone-400">{formattedDate}</span>
              <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${cfg.bg} ${cfg.text}`}>
                {cfg.label}
              </span>
              {localStatus === "ready_for_publish" && !inPlan && (
                <span className="text-xs text-stone-400">· Für Veröffentlichung vorbereitet</span>
              )}
              {inPlan && (
                <span className="text-xs px-1.5 py-0.5 rounded-full font-medium bg-violet-100 text-violet-700">
                  Im Monatsplan
                </span>
              )}
            </div>
            {/* Vorschau-Text */}
            <p className="text-sm text-stone-700 leading-snug line-clamp-2">
              {editText || draft.draft_text}
            </p>
          </div>
          {/* Aktionen */}
          <div className="flex items-center gap-2 flex-shrink-0" onClick={e => e.stopPropagation()}>
            <button
              onClick={() => setExpanded(e => !e)}
              className="flex items-center gap-1 text-xs text-stone-400 hover:text-stone-600 transition-colors"
              title={expanded ? "Schließen" : "Öffnen"}
            >
              {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
            <button
              onClick={() => onDelete(draft.draft_id)}
              className="text-stone-300 hover:text-red-400 transition-colors"
              title="Entwurf löschen"
            >
              <Trash2 size={13} />
            </button>
          </div>
        </div>
      </div>

      {/* ── Detail-/Bearbeitungsansicht ───────────────────────────────── */}
      {expanded && (
        <div className="border-t border-stone-100 bg-stone-50 p-4 space-y-4">

          {/* Herkunfts-Kontext */}
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2.5">
            <Sparkles size={12} className="flex-shrink-0 mt-0.5 text-amber-500" />
            <div className="text-xs text-stone-500 leading-relaxed">
              <span className="font-medium text-stone-600">Aus Evergreen-Refresh</span>
              {draft.platform && (
                <span> · ursprünglicher {draft.platform}-Post mit unterdurchschnittlichem Engagement</span>
              )}
              {draft.rationale && (
                <span className="block mt-0.5 text-stone-400">{draft.rationale}</span>
              )}
            </div>
          </div>

          {/* Original vs. Entwurf */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <p className="text-xs font-medium text-stone-400 mb-1.5">Original</p>
              <div className="bg-white rounded-lg border border-stone-200 p-3 text-sm text-stone-400 leading-relaxed whitespace-pre-wrap min-h-[100px]">
                {draft.original_text || "—"}
              </div>
            </div>
            <div>
              <p className="text-xs font-medium text-green-600 mb-1.5">
                Überarbeiteter Entwurf
                {isEditing && (
                  <span className="ml-1 text-stone-400 font-normal">(wird bearbeitet)</span>
                )}
              </p>
              {isEditing ? (
                <textarea
                  value={editText}
                  onChange={e => setEditText(e.target.value)}
                  rows={6}
                  className="w-full bg-white rounded-lg border border-green-300 p-3 text-sm text-stone-700 leading-relaxed resize-none focus:outline-none focus:ring-2 focus:ring-green-300"
                  autoFocus
                />
              ) : (
                <div className="bg-green-50 rounded-lg border border-green-200 p-3 text-sm text-stone-700 leading-relaxed whitespace-pre-wrap min-h-[100px]">
                  {editText}
                </div>
              )}
            </div>
          </div>

          {/* Aktions-Buttons */}
          <div className="flex items-center gap-2 flex-wrap pt-1 border-t border-stone-100">
            {isEditing ? (
              <>
                <button
                  onClick={handleSave}
                  disabled={saving || !editText.trim()}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-stone-800 text-white text-xs font-semibold rounded-lg hover:bg-stone-900 disabled:opacity-50 transition-colors"
                >
                  {saving
                    ? <Loader2 size={12} className="animate-spin" />
                    : <CheckCircle size={12} />}
                  Änderungen speichern
                </button>
                <button
                  onClick={handleCancelEdit}
                  disabled={saving}
                  className="px-3 py-1.5 text-xs text-stone-500 hover:text-stone-700 border border-stone-200 rounded-lg transition-colors"
                >
                  Abbrechen
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setIsEditing(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-stone-300 text-stone-600 text-xs font-medium rounded-lg hover:border-stone-400 transition-colors"
                >
                  <Edit3 size={12} />
                  Weiterbearbeiten
                </button>

                {localStatus !== "ready_for_publish" ? (
                  <button
                    onClick={handleMarkReady}
                    disabled={saving}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white text-xs font-semibold rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                  >
                    {saving
                      ? <Loader2 size={12} className="animate-spin" />
                      : <CheckCircle size={12} />}
                    Für Veröffentlichung vorbereiten
                  </button>
                ) : (
                  /* ready_for_publish: Monatsplan-CTA oder Bestätigung */
                  !inPlan ? (
                    <button
                      onClick={() => setShowPlanForm(f => !f)}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-600 text-white text-xs font-semibold rounded-lg hover:bg-violet-700 transition-colors"
                    >
                      <CalendarPlus size={12} />
                      In Monatsplan übernehmen
                    </button>
                  ) : (
                    <span className="flex items-center gap-1.5 text-xs text-violet-700 font-medium px-3 py-1.5 bg-violet-50 rounded-lg border border-violet-200">
                      <CheckCircle size={12} />
                      Im Monatsplan eingetragen
                      <NavLink
                        to="/monthly-plan"
                        className="ml-1 underline underline-offset-2 hover:no-underline transition-all"
                        onClick={e => e.stopPropagation()}
                      >
                        Ansehen →
                      </NavLink>
                    </span>
                  )
                )}

                <button
                  onClick={() => onDelete(draft.draft_id)}
                  className="ml-auto flex items-center gap-1 text-xs text-stone-300 hover:text-red-400 transition-colors"
                  title="Entwurf löschen"
                >
                  <Trash2 size={12} />
                  Löschen
                </button>
              </>
            )}
          </div>

          {/* ── Inline Monatsplan-Formular (Block 12) ─────────────────── */}
          {showPlanForm && !inPlan && (
            <div className="bg-violet-50 border border-violet-100 rounded-xl p-4 space-y-3 mt-1">
              <div className="flex items-center gap-2 mb-1">
                <CalendarPlus size={13} className="text-violet-600 flex-shrink-0" />
                <p className="text-xs font-semibold text-violet-700">In Monatsplan eintragen</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {/* Datum */}
                <div>
                  <label className="text-xs text-stone-500 mb-1 block font-medium">Datum</label>
                  <input
                    type="date"
                    value={planDate}
                    min={new Date().toISOString().split("T")[0]}
                    onChange={e => setPlanDate(e.target.value)}
                    className="w-full text-xs border border-stone-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-violet-300 bg-white"
                  />
                </div>

                {/* Uhrzeit */}
                <div>
                  <label className="text-xs text-stone-500 mb-1 block font-medium">Uhrzeit</label>
                  <select
                    value={planTime}
                    onChange={e => setPlanTime(e.target.value)}
                    className="w-full text-xs border border-stone-200 rounded-lg px-2 py-1.5 bg-white"
                  >
                    {PLAN_TIMES.map(t => (
                      <option key={t} value={t}>{t} Uhr</option>
                    ))}
                  </select>
                </div>

                {/* Plattform */}
                <div>
                  <label className="text-xs text-stone-500 mb-1 block font-medium">Plattform</label>
                  <select
                    value={planPlatform}
                    onChange={e => setPlanPlatform(e.target.value)}
                    className="w-full text-xs border border-stone-200 rounded-lg px-2 py-1.5 bg-white"
                  >
                    <option value="instagram">Instagram</option>
                    <option value="facebook">Facebook</option>
                    <option value="linkedin">LinkedIn</option>
                    <option value="tiktok">TikTok</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center gap-2 pt-1">
                <button
                  onClick={handleAddToPlan}
                  disabled={addingToPlan || !planDate}
                  className="flex items-center gap-1.5 px-4 py-1.5 bg-violet-600 text-white text-xs font-semibold rounded-lg hover:bg-violet-700 disabled:opacity-50 transition-colors"
                >
                  {addingToPlan
                    ? <Loader2 size={12} className="animate-spin" />
                    : <CheckCircle size={12} />}
                  In Monatsplan eintragen
                </button>
                <button
                  onClick={() => setShowPlanForm(false)}
                  className="text-xs text-stone-400 hover:text-stone-600 transition-colors"
                >
                  Abbrechen
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};


// ── Hauptkomponente ───────────────────────────────────────────────────────────

const ContentToolsPage = () => {
  // Feature-Gates
  const contentToolsGate = useFeatureGate("content_tools");
  const evergreenGate    = useFeatureGate("evergreen");

  // ── Hook-Generator State ─────────────────────────────────────────
  const [hookTopic, setHookTopic]     = useState('');
  const [hookAudience, setHookAudience] = useState('');
  const [hookChannel, setHookChannel] = useState('instagram');
  const [hookResult, setHookResult]   = useState(null);
  const [hookLoading, setHookLoading] = useState(false);

  // ── Pain-Point State ─────────────────────────────────────────────
  const [painAudience, setPainAudience] = useState('arbeitssuchende');
  const [painOffering, setPainOffering] = useState('Bewerbungscoaching');
  const [painContext, setPainContext]   = useState('');
  const [painResult, setPainResult]     = useState(null);
  const [painLoading, setPainLoading]   = useState(false);

  // ── Manychat State ───────────────────────────────────────────────
  const [testComment, setTestComment]   = useState('');
  const [manychatResult, setManychatResult] = useState(null);
  const [manychatStatus, setManychatStatus] = useState(null);

  const [audiences, setAudiences] = useState([]);

  // ── Draft-Workspace State ────────────────────────────────────────
  const [drafts, setDrafts]           = useState([]);
  const [draftsLoading, setDraftsLoading] = useState(false);
  const [draftsError, setDraftsError] = useState(null);

  // ── Drafts laden ─────────────────────────────────────────────────
  const loadDrafts = useCallback(async () => {
    if (!evergreenGate.allowed) return;
    setDraftsLoading(true);
    setDraftsError(null);
    try {
      const res = await axios.get(`${API}/content-tools/refresh/drafts`);
      setDrafts(res.data.drafts || []);
    } catch {
      setDraftsError("Entwürfe konnten nicht geladen werden.");
    } finally {
      setDraftsLoading(false);
    }
  }, [evergreenGate.allowed]);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [audiencesRes, manychatRes] = await Promise.all([
          axios.get(`${API}/content-tools/pain-point/audiences`),
          axios.get(`${API}/content-tools/manychat/status`)
        ]);
        setAudiences(audiencesRes.data.audiences || []);
        setManychatStatus(manychatRes.data);
      } catch {
        { /* Fetch fehlgeschlagen – kein kritischer Fehler */ }
      }
    };
    fetchOptions();
    loadDrafts();
  }, [loadDrafts]);

  // ── Draft-Handler ────────────────────────────────────────────────

  const handleUpdateDraft = async (draftId, newText, newStatus) => {
    await axios.patch(`${API}/content-tools/refresh/drafts/${draftId}`, {
      draft_text: newText,
      status: newStatus,
    });
    setDrafts(prev =>
      prev.map(d =>
        d.draft_id === draftId
          ? { ...d, draft_text: newText, status: newStatus }
          : d
      )
    );
    if (newStatus === "ready_for_publish") {
      toast.success("Als veröffentlichungsbereit markiert.");
    } else {
      toast.success("Entwurf gespeichert.");
    }
  };

  const handleDeleteDraft = async (draftId) => {
    try {
      await axios.delete(`${API}/content-tools/refresh/drafts/${draftId}`);
      setDrafts(prev => prev.filter(d => d.draft_id !== draftId));
      toast.success("Entwurf gelöscht.");
    } catch {
      toast.error("Fehler beim Löschen des Entwurfs.");
    }
  };

  // ── Hook-Generator ───────────────────────────────────────────────

  const handleGenerateHook = async () => {
    if (!hookTopic.trim()) {
      toast.error('Bitte gib ein Thema ein');
      return;
    }
    setHookLoading(true);
    try {
      const response = await axios.post(`${API}/content-tools/hook/generate`, {
        topic: hookTopic,
        target_audience: hookAudience || null,
        channel: hookChannel
      });
      setHookResult(response.data);
      toast.success('Hook generiert!');
    } catch {
      toast.error('Fehler beim Generieren');
    } finally {
      setHookLoading(false);
    }
  };

  // ── Pain-Point-Analyse ───────────────────────────────────────────

  const handleAnalyzePainPoints = async () => {
    setPainLoading(true);
    try {
      const response = await axios.post(`${API}/content-tools/pain-point/analyze`, {
        target_audience: painAudience,
        offering: painOffering,
        additional_context: painContext || null
      });
      setPainResult(response.data);
      toast.success(`${response.data.total_ideas} Content-Ideen generiert!`);
    } catch {
      toast.error('Fehler bei der Analyse');
    } finally {
      setPainLoading(false);
    }
  };

  // ── Manychat ─────────────────────────────────────────────────────

  const handleTestManychat = async () => {
    if (!testComment.trim()) {
      toast.error('Bitte gib einen Test-Kommentar ein');
      return;
    }
    try {
      const response = await axios.post(`${API}/content-tools/manychat/test-trigger`, {
        comment_text: testComment,
        user_name: 'Test-User'
      });
      setManychatResult(response.data);
      if (response.data.triggered) {
        toast.success(`Trigger erkannt: ${response.data.trigger_key}`);
      } else {
        toast.info('Kein Keyword-Trigger erkannt');
      }
    } catch {
      toast.error('Fehler beim Testen');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('In Zwischenablage kopiert!');
  };

  // ── Render ───────────────────────────────────────────────────────

  // Anzahl Drafts nach Status für Header-Badge
  const readyCount = drafts.filter(d => d.status === "ready_for_publish").length;

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="font-heading text-3xl font-bold">Content Tools</h1>
        <p className="text-stone-500">Überarbeitete Entwürfe, Hook-Generator, Pain-Point-Analyse &amp; Manychat</p>
      </div>

      {/* Upgrade-Nudge für Content Tools */}
      {!contentToolsGate.allowed && (
        <UpgradeNudge
          {...contentToolsGate}
          featureLabel="Content Tools (Pain-Point-Analyse & Hook-Generator)"
        />
      )}

      {/* ══════════════════════════════════════════════════════════════
          Überarbeitete Entwürfe — Draft-Workspace (Block 11)
          Gate: evergreen (Creator+ / Trial)
          ══════════════════════════════════════════════════════════════ */}
      <div className="bg-white rounded-xl border border-stone-200 p-6">
        <div className="flex items-start gap-3 mb-5">
          <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <FileText size={20} className="text-green-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-heading text-xl font-bold">Überarbeitete Entwürfe</h2>
              {evergreenGate.allowed && drafts.length > 0 && (
                <span className="text-xs px-2 py-0.5 bg-stone-100 text-stone-500 rounded-full font-medium">
                  {drafts.length} {drafts.length === 1 ? "Entwurf" : "Entwürfe"}
                  {readyCount > 0 && (
                    <span className="ml-1 text-green-600">· {readyCount} bereit</span>
                  )}
                </span>
              )}
            </div>
            <p className="text-sm text-stone-500 mt-0.5">
              Akzeptierte KI-Refresh-Entwürfe aus dem Evergreen-Workflow — hier weiterbearbeiten und vorbereiten
            </p>
          </div>
          {evergreenGate.allowed && (
            <button
              onClick={loadDrafts}
              disabled={draftsLoading}
              className="flex-shrink-0 p-2 text-stone-400 hover:text-stone-600 hover:bg-stone-50 rounded-lg transition-colors"
              title="Entwürfe aktualisieren"
            >
              <RefreshCw size={15} className={draftsLoading ? "animate-spin" : ""} />
            </button>
          )}
        </div>

        {/* Gate: nicht berechtigt */}
        {!evergreenGate.allowed ? (
          <UpgradeNudge
            {...evergreenGate}
            featureLabel="Evergreen-Refresh & Draft-Workspace"
          />
        ) : draftsLoading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 size={24} className="animate-spin text-stone-300" />
          </div>
        ) : draftsError ? (
          <div className="flex items-center gap-3 bg-red-50 border border-red-100 rounded-lg px-4 py-3">
            <p className="text-sm text-red-500 flex-1">{draftsError}</p>
            <button
              onClick={loadDrafts}
              className="text-xs text-red-500 hover:text-red-700 font-medium underline-offset-2 hover:underline"
            >
              Erneut versuchen
            </button>
          </div>
        ) : drafts.length === 0 ? (
          /* Empty State */
          <div className="text-center py-10 text-stone-400">
            <FileText size={36} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm font-medium text-stone-500 mb-1">
              Noch keine überarbeiteten Entwürfe
            </p>
            <p className="text-xs text-stone-400 mb-4 max-w-sm mx-auto">
              Markiere schwach-performende Posts in Analytics, öffne sie in Evergreen
              und starte einen KI-Refresh — akzeptierte Entwürfe erscheinen hier.
            </p>
            <div className="flex items-center justify-center gap-4">
              <NavLink
                to="/analytics"
                className="inline-flex items-center gap-1.5 text-xs text-amber-600 hover:text-amber-700 font-medium transition-colors"
              >
                Zu Analytics
                <ArrowRight size={12} />
              </NavLink>
              <span className="text-stone-200">|</span>
              <NavLink
                to="/evergreen"
                className="inline-flex items-center gap-1.5 text-xs text-green-600 hover:text-green-700 font-medium transition-colors"
              >
                Zu Evergreen
                <ArrowRight size={12} />
              </NavLink>
            </div>
          </div>
        ) : (
          /* Draft-Liste */
          <div className="space-y-3">
            {drafts.map(draft => (
              <RefreshDraftCard
                key={draft.draft_id}
                draft={draft}
                onUpdate={handleUpdateDraft}
                onDelete={handleDeleteDraft}
              />
            ))}
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════════════
          Hook Generator
          ══════════════════════════════════════════════════════════════ */}
      <div className="bg-white rounded-xl border border-stone-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
            <Zap size={20} className="text-orange-600" />
          </div>
          <div>
            <h2 className="font-heading text-xl font-bold">Hook-Generator</h2>
            <p className="text-sm text-stone-500">Generiere aufmerksamkeitsstarke Hooks (Pain Point + Neugier)</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Thema *</label>
            <input
              type="text"
              value={hookTopic}
              onChange={(e) => setHookTopic(e.target.value)}
              placeholder="z.B. AVGS Coaching, Bewerbungstipps"
              className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Zielgruppe (optional)</label>
            <input
              type="text"
              value={hookAudience}
              onChange={(e) => setHookAudience(e.target.value)}
              placeholder="z.B. Jobcenter, 50+, Gründer"
              className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Kanal</label>
            <select
              value={hookChannel}
              onChange={(e) => setHookChannel(e.target.value)}
              className="w-full px-3 py-2 border border-stone-300 rounded-lg"
            >
              <option value="instagram">Instagram</option>
              <option value="facebook">Facebook</option>
              <option value="linkedin">LinkedIn</option>
              <option value="tiktok">TikTok</option>
            </select>
          </div>
        </div>

        <button
          onClick={handleGenerateHook}
          disabled={hookLoading || !hookTopic.trim()}
          className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 flex items-center gap-2"
        >
          {hookLoading ? <Loader2 className="animate-spin" size={16} /> : <Zap size={16} />}
          Hook generieren
        </button>

        {hookResult && (
          <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
            <div className="flex items-start justify-between mb-2">
              <span className="text-xs px-2 py-0.5 bg-orange-200 text-orange-800 rounded">
                {hookResult.detected_audience} · {hookResult.channel}
              </span>
              <button onClick={() => copyToClipboard(hookResult.hook)} className="text-orange-600 hover:text-orange-700">
                <Copy size={16} />
              </button>
            </div>
            <p className="font-medium text-stone-900 whitespace-pre-wrap">{hookResult.hook}</p>

            {hookResult.alternatives?.length > 0 && (
              <div className="mt-3 pt-3 border-t border-orange-200">
                <p className="text-xs font-medium text-stone-500 mb-2">Alternativen:</p>
                <div className="space-y-2">
                  {hookResult.alternatives.map((alt, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-white rounded">
                      <span className="text-sm">{alt}</span>
                      <button onClick={() => copyToClipboard(alt)} className="text-stone-400 hover:text-stone-600">
                        <Copy size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════════════
          Pain Point Analyzer
          ══════════════════════════════════════════════════════════════ */}
      <div className="bg-white rounded-xl border border-stone-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
            <TrendingUp size={20} className="text-purple-600" />
          </div>
          <div>
            <h2 className="font-heading text-xl font-bold">Pain-Point-Analyse</h2>
            <p className="text-sm text-stone-500">Analysiere Zielgruppen und generiere Content-Ideen</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Zielgruppe</label>
            <select
              value={painAudience}
              onChange={(e) => setPainAudience(e.target.value)}
              className="w-full px-3 py-2 border border-stone-300 rounded-lg"
            >
              {audiences.map((a) => <option key={a.key} value={a.key}>{a.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Angebot</label>
            <select
              value={painOffering}
              onChange={(e) => setPainOffering(e.target.value)}
              className="w-full px-3 py-2 border border-stone-300 rounded-lg"
            >
              <option value="Bewerbungscoaching">Bewerbungscoaching</option>
              <option value="Gründungscoaching">Gründungscoaching</option>
              <option value="Karriereberatung">Karriereberatung</option>
              <option value="AVGS-Coaching">AVGS-Coaching</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Zusätzlicher Kontext</label>
            <input
              type="text"
              value={painContext}
              onChange={(e) => setPainContext(e.target.value)}
              placeholder="z.B. häufige Kommentare"
              className="w-full px-3 py-2 border border-stone-300 rounded-lg"
            />
          </div>
        </div>

        <button
          onClick={handleAnalyzePainPoints}
          disabled={painLoading}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
        >
          {painLoading ? <Loader2 className="animate-spin" size={16} /> : <Search size={16} />}
          Pain Points analysieren
        </button>

        {painResult && (
          <div className="mt-4 space-y-4">
            <BrandContextHint show={painResult.brand_context_used} />
            <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
              <h4 className="font-medium text-purple-900 mb-2">{painResult.profile_name}</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-medium text-purple-700 mb-1">Top Pain Points:</p>
                  <ul className="text-sm text-stone-700 space-y-1">
                    {painResult.pain_points?.slice(0, 3).map((p, i) => <li key={i}>❌ {p}</li>)}
                  </ul>
                </div>
                <div>
                  <p className="text-xs font-medium text-purple-700 mb-1">Wünsche:</p>
                  <ul className="text-sm text-stone-700 space-y-1">
                    {painResult.desires?.slice(0, 3).map((d, i) => <li key={i}>✅ {d}</li>)}
                  </ul>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-medium text-stone-900 mb-2">{painResult.total_ideas} Content-Ideen:</h4>
              <div className="space-y-3">
                {painResult.content_ideas?.map((idea) => (
                  <div key={idea.id} className="p-4 bg-stone-50 border border-stone-200 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded">{idea.template}</span>
                          <span className="text-xs text-stone-500">{idea.channels_recommended?.join(', ')}</span>
                        </div>
                        <p className="text-sm font-medium text-stone-900">{idea.pain_point}</p>
                        <p className="text-sm text-stone-600 mt-1">{idea.content_angle}</p>
                        <p className="text-sm text-orange-700 mt-2 font-medium">{idea.hook_suggestion}</p>
                      </div>
                      <button
                        onClick={() => copyToClipboard(idea.hook_suggestion)}
                        className="text-stone-400 hover:text-stone-600 ml-2"
                      >
                        <Copy size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════════════
          Manychat Integration
          ══════════════════════════════════════════════════════════════ */}
      <div className="bg-white rounded-xl border border-stone-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
            <MessageSquare size={20} className="text-blue-600" />
          </div>
          <div>
            <h2 className="font-heading text-xl font-bold">Manychat Integration</h2>
            <p className="text-sm text-stone-500">Automatische DM-Antworten auf Keyword-Kommentare</p>
          </div>
          {manychatStatus && (
            <span className={`ml-auto text-xs px-2 py-1 rounded ${
              manychatStatus.is_active ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
            }`}>
              {manychatStatus.is_active ? '🟢 Aktiv' : '🔵 Vorbereitet'}
            </span>
          )}
        </div>

        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg mb-4">
          <p className="text-sm text-amber-800">
            <strong>⚠️ Status: Vorbereitet</strong><br />
            Die Manychat-Integration ist strukturiert und testbar, aber noch nicht aktiv.<br />
            Für Aktivierung: <code className="bg-amber-100 px-1 rounded">MANYCHAT_API_KEY</code> in .env setzen.
          </p>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-stone-700 mb-1">Test-Kommentar</label>
          <div className="flex gap-2">
            <input
              type="text"
              value={testComment}
              onChange={(e) => setTestComment(e.target.value)}
              placeholder="z.B. 'Ich brauche Hilfe bei meiner Bewerbung'"
              className="flex-1 px-3 py-2 border border-stone-300 rounded-lg"
            />
            <button
              onClick={handleTestManychat}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <Send size={16} /> Testen
            </button>
          </div>
        </div>

        {manychatResult && (
          <div className={`p-4 rounded-lg ${manychatResult.triggered ? 'bg-green-50 border border-green-200' : 'bg-stone-50 border border-stone-200'}`}>
            {manychatResult.triggered ? (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle size={16} className="text-green-600" />
                  <span className="font-medium text-green-800">Trigger erkannt: {manychatResult.trigger_key}</span>
                </div>
                <p className="text-xs text-stone-500 mb-2">Keyword: "{manychatResult.matched_keyword}"</p>
                <div className="p-3 bg-white rounded border border-green-100">
                  <p className="text-xs font-medium text-stone-500 mb-1">Vorbereitete DM:</p>
                  <pre className="text-sm whitespace-pre-wrap text-stone-700">{manychatResult.dm_prepared}</pre>
                </div>
                <p className="text-xs text-amber-600 mt-2">{manychatResult.note}</p>
              </>
            ) : (
              <p className="text-stone-600">Kein Keyword-Trigger erkannt in diesem Kommentar.</p>
            )}
          </div>
        )}

        <div className="mt-4 pt-4 border-t">
          <p className="text-sm font-medium text-stone-700 mb-2">Verfügbare Keyword-Trigger:</p>
          <div className="flex flex-wrap gap-2">
            {manychatStatus?.available_triggers?.map((trigger) => (
              <span key={trigger} className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded">{trigger}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentToolsPage;
